import React, {memo, useEffect, useMemo, useRef, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useHistory, useLocation} from 'react-router-dom';
import {StyledInfiniteScroll} from 'routes/App/discountsV2/DiscountsList/DiscountsView/styles';
import {Formik} from 'formik';
import moment from 'moment';
import DiscountsTable from 'routes/App/discountsV2/DiscountsList/DiscountsTable/DiscountsTable';
import DiscountsToolbar from 'routes/App/discountsV2/DiscountsList/DiscountsToolbar';
import {
  getDiscounts,
  getDiscountsLoading,
  getDiscountsPage,
  getDiscountsSummary,
  getDiscountStatistics,
  getHasNextPage,
  getLocationId,
  getOrderBySorting,
  getSortDirection,
  getEmailMarketingTrialDate,
  getDiscount,
  getButtonStatus,
  getIsOpenModal
} from 'store/discountsV2/selectors';
import {
  CLEAR_DISCOUNT,
  CLEAR_DISCOUNTS,
  CREATE_DISCOUNT,
  GET_DISCOUNTS,
  GET_DISCOUNTS_SUMMARY,
  GET_DISCOUNT_STATISTICS,
  PUSH_MODALS_STACK,
  SET_BUTTON_STATUS,
  SET_DISCOUNT,
  SET_DISCOUNT_MODAL,
  SET_SORTING,
  UPDATE_DISCOUNT
} from 'store/discountsV2/action';
import {
  DISCOUNT_MODALS_V2,
  DISCOUNT_POSTHOG_EVENTS,
  DISCOUNT_STATUS_FILTER,
  DISCOUNT_TYPE
} from 'helpers/constants/discountsV2';
import {OrdersContainer} from 'routes/App/orders/OrdersList/styles';
import ButtonScrollTop from 'components/buttons/ButtonScrollTop';
import DiscountV2Schema from 'helpers/services/formValidationSchemas/DiscountV2Schema';
import {ConnectedFocusError} from 'focus-formik-error';
import {DaysPickerContextProvider} from 'routes/App/discountsV2/DiscountWizard/DateAvailability/context/DaysPickerContext';
import SettingsModal from 'routes/App/discountsV2/DiscountWizard/SettingsModal';
import BogoSettingsModal from 'routes/App/discountsV2/DiscountWizard/BogoSettingsModal';
import ItemSettingsModal from 'routes/App/discountsV2/DiscountWizard/ItemSettingsModal';
import {getDiscountTypeForPostHog} from 'helpers/functionUtils/discountsV2';
import {HintText} from 'components/HintText/styles';

const DiscountsView = () => {
  const dispatch = useDispatch();
  const locationId = getLocationId();
  const discounts = getDiscounts();
  const hasNextPage = getHasNextPage();
  const discountsPage = getDiscountsPage();
  const loading = getDiscountsLoading();
  const orderBy = getOrderBySorting();
  const discountDirection = getSortDirection();
  const emailMarketingTrialDate = getEmailMarketingTrialDate();
  const {
    AllowedServiceFeatures,
    Timezone,
    _id,
    BusinessName,
    FullName
  } = useSelector((state) => state.user.activeLocation);
  const {userData} = useSelector((state) => state.user);

  const formikRef = useRef();
  const location = useLocation();
  const history = useHistory();
  const discount = getDiscount();

  const statusFilter = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const raw = params.get('status');
    if (raw === DISCOUNT_STATUS_FILTER.ARCHIVED) {
      return DISCOUNT_STATUS_FILTER.ARCHIVED;
    }
    if (raw === DISCOUNT_STATUS_FILTER.ALL) {
      return DISCOUNT_STATUS_FILTER.ALL;
    }
    return DISCOUNT_STATUS_FILTER.ACTIVE;
  }, [location.search]);

  const handleStatusChange = (next) => {
    const params = new URLSearchParams(location.search);
    if (next === DISCOUNT_STATUS_FILTER.ACTIVE) {
      params.delete('status');
    } else {
      params.set('status', next);
    }
    const search = params.toString();
    history.replace(`${location.pathname}${search ? `?${search}` : ''}`);
  };
  const buttonStatus = getButtonStatus();
  const isOpenModal = getIsOpenModal();
  const [enabledDiscount, setEnabledDiscount] = useState(false);
  const [enabledDate, setEnabledDate] = useState(false);
  const [enabledCustomAvailability, setEnabledCustomAvailability] = useState(
    false
  );
  const [enabledAdvancedSettings, setEnabledAdvancedSettings] = useState(false);

  useEffect(() => {
    if (discount?.name) {
      setEnabledCustomAvailability(
        discount?.availability?.activatesAtLocal ||
          discount?.availability?.deactivatesAtLocal ||
          discount?.availability?.weekly !== null
      );
      setEnabledDate(
        discount?.availability?.activatesAtLocal ||
          discount?.availability?.deactivatesAtLocal
      );
      setEnabledDiscount(
        discount.promoCode !== null && discount.promoCode?.code
      );
      setEnabledAdvancedSettings(discount?.availability?.weekly !== null);
    }
  }, [discount]);

  const summary = getDiscountsSummary();
  const statistics = getDiscountStatistics();

  useEffect(() => {
    dispatch(GET_DISCOUNTS_SUMMARY());
  }, [locationId]);

  useEffect(() => {
    dispatch(GET_DISCOUNT_STATISTICS());
  }, [locationId, statusFilter]);

  useEffect(() => {
    dispatch(CLEAR_DISCOUNTS());
    dispatch(
      GET_DISCOUNTS({
        page: 0,
        sort: {sortBy: orderBy, sort: discountDirection},
        sortDirection: discountDirection,
        limit: 50,
        ...(statusFilter !== DISCOUNT_STATUS_FILTER.ALL && {
          status: statusFilter
        })
      })
    );
    return () => dispatch(CLEAR_DISCOUNTS());
  }, [locationId, statusFilter]);

  const onSort = (sort) => {
    dispatch(SET_SORTING(sort));
    dispatch(CLEAR_DISCOUNTS());
    dispatch(
      GET_DISCOUNTS({
        sort,
        limit: 50,
        page: 0,
        ...(statusFilter !== DISCOUNT_STATUS_FILTER.ALL && {
          status: statusFilter
        })
      })
    );
  };

  const popperActions = {
    editAction: (d) => {
      dispatch(SET_DISCOUNT(d));
      dispatch(SET_DISCOUNT_MODAL(true));
    },
    duplicateAction: (d) => {
      const duplicate = {...d};
      delete duplicate.id;
      delete duplicate.status;
      duplicate.name = `Duplicate ${duplicate.name}`;
      dispatch(SET_DISCOUNT(duplicate));
      dispatch(SET_DISCOUNT_MODAL(true));
    },
    disableAction: (d) =>
      dispatch(PUSH_MODALS_STACK({type: DISCOUNT_MODALS_V2.DISABLE, d})),
    deleteAction: (d) =>
      dispatch(PUSH_MODALS_STACK({type: DISCOUNT_MODALS_V2.DELETE, d}))
  };

  const onScrollChange = () => {
    dispatch(
      GET_DISCOUNTS({
        page: discountsPage,
        sort: {sortBy: orderBy, sort: discountDirection},
        limit: 50,
        ...(statusFilter !== DISCOUNT_STATUS_FILTER.ALL && {
          status: statusFilter
        })
      })
    );
  };

  const onCloseAction = () => {
    dispatch(CLEAR_DISCOUNT());
    formikRef?.current?.resetForm();
    dispatch(SET_DISCOUNT_MODAL(false));
    setEnabledCustomAvailability(false);
    setEnabledAdvancedSettings(false);
    setEnabledDate(false);
    setEnabledDiscount(false);
  };

  return (
    <StyledInfiniteScroll
      dataLength={discounts.length}
      next={onScrollChange}
      hasMore={hasNextPage}
    >
      <OrdersContainer>
        <DiscountsToolbar summary={summary} statistics={statistics} />
        {statusFilter === DISCOUNT_STATUS_FILTER.ARCHIVED && (
          <HintText className='mTop10'>
            <span>Order numbers are counted from January 1, 2026</span>
          </HintText>
        )}
        <DiscountsTable
          discounts={discounts}
          loading={loading}
          onSort={onSort}
          orderBy={orderBy}
          discountDirection={discountDirection}
          popperActions={popperActions}
          allowedServiceFeatures={AllowedServiceFeatures}
          emailMarketingTrialDate={emailMarketingTrialDate}
          timezone={Timezone}
          statistics={statistics}
          status={statusFilter}
          onStatusChange={handleStatusChange}
        />
        <Formik
          initialValues={{
            ...discount
          }}
          innerRef={formikRef}
          validationSchema={DiscountV2Schema}
          enableReinitialize
          onSubmit={async (
            values,
            {setFieldError, setSubmitting, validateForm}
          ) => {
            const payload = {...values};
            const schemaErrors = await validateForm();
            if (schemaErrors && Object.keys(schemaErrors).length > 0) {
              setSubmitting(false);
              if (window?.posthog) {
                window?.posthog.capture(DISCOUNT_POSTHOG_EVENTS.SAVE, {
                  result: 'failed',
                  mode: discount.id ? 'edit' : 'new',
                  discount_type: getDiscountTypeForPostHog(payload),
                  location_id: _id,
                  location_name: BusinessName || FullName,
                  user_id: userData?._id,
                  user_email: userData?.email,
                  timestamp: moment().utc().format()
                });
              }
              return;
            }

            if (enabledDiscount && !payload?.promoCode?.code) {
              setFieldError(
                'promoCode.code',
                'Promo Code field cannot be empty!'
              );
              setSubmitting(false);
              if (window?.posthog) {
                window?.posthog.capture(DISCOUNT_POSTHOG_EVENTS.SAVE, {
                  result: 'failed',
                  mode: discount.id ? 'edit' : 'new',
                  discount_type: getDiscountTypeForPostHog(payload),
                  location_id: _id,
                  location_name: BusinessName || FullName,
                  user_id: userData?._id,
                  user_email: userData?.email,
                  timestamp: moment().utc().format()
                });
              }
              return;
            }

            if (
              (enabledDate &&
                (!payload?.availability?.activatesAtLocal ||
                  payload?.availability?.activatesAtLocal ===
                    'Invalid date')) ||
              (enabledDate &&
                (!payload?.availability?.deactivatesAtLocal ||
                  payload?.availability?.deactivatesAtLocal === 'Invalid date'))
            ) {
              setFieldError(
                'availability.activatesAtLocal',
                'Please select start and end date!'
              );
              setSubmitting(false);
              if (window?.posthog) {
                window?.posthog.capture(DISCOUNT_POSTHOG_EVENTS.SAVE, {
                  result: 'failed',
                  mode: discount.id ? 'edit' : 'new',
                  discount_type: getDiscountTypeForPostHog(payload),
                  location_id: _id,
                  location_name: BusinessName || FullName,
                  user_id: userData?._id,
                  user_email: userData?.email,
                  timestamp: moment().utc().format()
                });
              }
              return;
            }

            if (
              (payload?.rate?.type === DISCOUNT_TYPE.BOGO ||
                payload?.type === DISCOUNT_TYPE.BOGO) &&
              (!payload?.bogo?.discounted || !payload?.bogo?.discounted?.length)
            ) {
              setFieldError(
                'bogo.discounted',
                'Please select items for BOGO discount!'
              );
              setSubmitting(false);
              if (window?.posthog) {
                window?.posthog.capture(DISCOUNT_POSTHOG_EVENTS.SAVE, {
                  result: 'failed',
                  mode: discount.id ? 'edit' : 'new',
                  discount_type: getDiscountTypeForPostHog(payload),
                  location_id: _id,
                  location_name: BusinessName || FullName,
                  user_id: userData?._id,
                  user_email: userData?.email,
                  timestamp: moment().utc().format()
                });
              }
              return;
            }

            if (
              (payload?.rate?.type === DISCOUNT_TYPE.SPECIFIC_ITEMS ||
                payload?.type === DISCOUNT_TYPE.SPECIFIC_ITEMS) &&
              (!payload?.specificItems?.discounted ||
                !payload?.specificItems?.discounted?.length)
            ) {
              setFieldError(
                'specificItems.discounted',
                'Please select items for discount!'
              );
              setSubmitting(false);
              if (window?.posthog) {
                window?.posthog.capture(DISCOUNT_POSTHOG_EVENTS.SAVE, {
                  result: 'failed',
                  mode: discount.id ? 'edit' : 'new',
                  discount_type: getDiscountTypeForPostHog(payload),
                  location_id: _id,
                  location_name: BusinessName || FullName,
                  user_id: userData?._id,
                  user_email: userData?.email,
                  timestamp: moment().utc().format()
                });
              }
              return;
            }

            if (payload?.promoCode?.code) {
              payload.promoCode.code = payload?.promoCode?.code?.toUpperCase();
            }

            if (
              payload?.rate?.type === DISCOUNT_TYPE.PERCENTAGE &&
              !payload?.subtotal?.min
            ) {
              payload.subtotal.min = 0;
            }
            if (
              payload?.rate?.type === DISCOUNT_TYPE.BOGO ||
              payload?.type === DISCOUNT_TYPE.BOGO
            ) {
              payload.type = DISCOUNT_TYPE.BOGO;
              payload.rate.type = DISCOUNT_TYPE.PERCENTAGE;
              payload.rate.value = 100;
            }

            if (!payload?.creationReason) {
              payload.creationReason = 'IncreaseOrderVolume';
            }

            if (payload?.promoCode !== null && payload?.promoCode?.usageLimit) {
              payload.promoCode.usageLimit.activatesAtLocal = payload?.promoCode
                ?.usageLimit?.activatesAtLocal
                ? payload.promoCode?.usageLimit?.activatesAtLocal
                : moment()
                    .utcOffset(Number(Timezone) || 0, false)
                    .utc(true)
                    .format();
            }
            if (!payload?.creationReason) {
              payload.creationReason = 'IncreaseOrderVolume';
            }

            dispatch(SET_BUTTON_STATUS(true));

            if (discount.id) {
              dispatch(
                UPDATE_DISCOUNT({
                  payload: {
                    id: discount.id,
                    discount: payload
                  },
                  postCallback: () => {
                    onCloseAction();
                    if (window?.posthog) {
                      window?.posthog.capture(DISCOUNT_POSTHOG_EVENTS.SAVE, {
                        result: 'success',
                        mode: 'edit',
                        discount_type: getDiscountTypeForPostHog(payload),
                        location_id: _id,
                        location_name: BusinessName || FullName,
                        user_id: userData?._id,
                        user_email: userData?.email,
                        timestamp: moment().utc().format()
                      });
                    }
                  },
                  errorCallback: () => {
                    if (window?.posthog) {
                      window?.posthog.capture(DISCOUNT_POSTHOG_EVENTS.SAVE, {
                        result: 'failed',
                        mode: 'edit',
                        discount_type: getDiscountTypeForPostHog(payload),
                        location_id: _id,
                        location_name: BusinessName || FullName,
                        user_id: userData?._id,
                        user_email: userData?.email,
                        timestamp: moment().utc().format()
                      });
                    }
                  }
                })
              );
            } else {
              dispatch(
                CREATE_DISCOUNT({
                  payload,
                  postCallback: () => {
                    onCloseAction();
                    if (window?.posthog) {
                      window?.posthog.capture(DISCOUNT_POSTHOG_EVENTS.SAVE, {
                        result: 'success',
                        mode: 'new',
                        discount_type: getDiscountTypeForPostHog(payload),
                        location_id: _id,
                        location_name: BusinessName || FullName,
                        user_id: userData?._id,
                        user_email: userData?.email,
                        timestamp: moment().utc().format()
                      });
                    }
                  },
                  locationPath: location.pathname,
                  errorCallback: () => {
                    if (window?.posthog) {
                      window?.posthog.capture(DISCOUNT_POSTHOG_EVENTS.SAVE, {
                        result: 'failed',
                        mode: 'new',
                        discount_type: getDiscountTypeForPostHog(payload),
                        location_id: _id,
                        location_name: BusinessName || FullName,
                        user_id: userData?._id,
                        user_email: userData?.email,
                        timestamp: moment().utc().format()
                      });
                    }
                  }
                })
              );
            }
          }}
        >
          {(formik) => {
            useEffect(() => {
              if (
                formik.isSubmitting &&
                Object.keys(formik.errors).length > 0
              ) {
                if (window?.posthog) {
                  window?.posthog.capture(DISCOUNT_POSTHOG_EVENTS.SAVE, {
                    result: 'failed',
                    mode: discount.id ? 'edit' : 'new',
                    discount_type: getDiscountTypeForPostHog(formik.values),
                    location_id: _id,
                    location_name: BusinessName || FullName,
                    user_id: userData?._id,
                    user_email: userData?.email,
                    timestamp: moment().utc().format()
                  });
                }
              }
            }, [formik.submitCount, formik.isSubmitting, formik.errors]);

            return (
              <>
                <ConnectedFocusError />
                <DaysPickerContextProvider>
                  {isOpenModal &&
                    (formik?.values?.type === DISCOUNT_TYPE.BOGO ||
                      formik?.values?.bogo?.discounted?.length) && (
                      <BogoSettingsModal
                        discount={discount}
                        formik={formik}
                        enabledDiscount={enabledDiscount}
                        setEnabledDiscount={setEnabledDiscount}
                        enabledDate={enabledDate}
                        setEnabledDate={setEnabledDate}
                        buttonStatus={buttonStatus}
                        allowedServiceFeatures={AllowedServiceFeatures}
                        enabledCustomAvailability={enabledCustomAvailability}
                        setEnabledCustomAvailability={
                          setEnabledCustomAvailability
                        }
                        enabledAdvancedSettings={enabledAdvancedSettings}
                        setEnabledAdvancedSettings={setEnabledAdvancedSettings}
                        isOpen={isOpenModal}
                        onCloseAction={onCloseAction}
                      />
                    )}
                  {isOpenModal &&
                    (formik?.values?.type === DISCOUNT_TYPE.SPECIFIC_ITEMS ||
                      formik?.values?.specificItems?.discounted?.length) && (
                      <ItemSettingsModal
                        discount={discount}
                        formik={formik}
                        enabledDiscount={enabledDiscount}
                        setEnabledDiscount={setEnabledDiscount}
                        enabledDate={enabledDate}
                        setEnabledDate={setEnabledDate}
                        buttonStatus={buttonStatus}
                        allowedServiceFeatures={AllowedServiceFeatures}
                        enabledCustomAvailability={enabledCustomAvailability}
                        setEnabledCustomAvailability={
                          setEnabledCustomAvailability
                        }
                        enabledAdvancedSettings={enabledAdvancedSettings}
                        setEnabledAdvancedSettings={setEnabledAdvancedSettings}
                        isOpen={isOpenModal}
                        onCloseAction={onCloseAction}
                      />
                    )}
                  {isOpenModal &&
                    formik?.values?.type === DISCOUNT_TYPE.WHOLE_CART && (
                      <SettingsModal
                        discount={discount}
                        formik={formik}
                        enabledDiscount={enabledDiscount}
                        setEnabledDiscount={setEnabledDiscount}
                        enabledDate={enabledDate}
                        setEnabledDate={setEnabledDate}
                        buttonStatus={buttonStatus}
                        allowedServiceFeatures={AllowedServiceFeatures}
                        enabledCustomAvailability={enabledCustomAvailability}
                        setEnabledCustomAvailability={
                          setEnabledCustomAvailability
                        }
                        enabledAdvancedSettings={enabledAdvancedSettings}
                        setEnabledAdvancedSettings={setEnabledAdvancedSettings}
                        isOpen={isOpenModal}
                        onCloseAction={onCloseAction}
                      />
                    )}
                </DaysPickerContextProvider>
              </>
            );
          }}
        </Formik>
      </OrdersContainer>
      <ButtonScrollTop />
    </StyledInfiniteScroll>
  );
};

export default memo(DiscountsView);
